/**
 * Centralized Account Layout Constants
 * 
 * This file contains all byte offsets, discriminators, and sizes for on-chain account parsing.
 * When Rust struct definitions change in the Solana program, update these values to match.
 * 
 * IMPORTANT: These values must match the Rust struct definitions in programs/popperverse/src/state.rs
 * Any change to field order, types, or sizes in Rust requires updating these constants.
 */

// ============================================================================
// DISCRIMINATORS
// These are the 8-byte account discriminators generated by Anchor.
// They are derived from: sha256("account:<AccountName>")[0..8]
// ============================================================================

export const DISCRIMINATORS = {
  // Account discriminators (from IDL)
  AUCTION_STATE: [252, 227, 205, 147, 72, 64, 250, 126],
  POPPER_NFT: [250, 185, 154, 82, 189, 107, 226, 175],
  VOTING_ROUND: [150, 130, 184, 126, 107, 147, 21, 170],
  NFT_VOTE_RECORD: [166, 106, 71, 14, 61, 62, 182, 232], // User vote tracking
  
  // Instruction discriminators (for transaction parsing if needed)
  INSTRUCTIONS: {
    PURCHASE: [21, 93, 113, 154, 193, 160, 242, 168],
    VOTE_FOR_NFT: [114, 105, 58, 63, 235, 121, 143, 223],
    VOTE_FOR_NFT_WITH_STATS: [236, 129, 56, 105, 70, 89, 215, 179],
    CREATE_DESIGN: [117, 172, 50, 80, 246, 123, 120, 146],
    DONATE: [121, 186, 218, 211, 73, 70, 196, 180],
    MINT_POPPER_NFT: [142, 96, 11, 94, 228, 28, 71, 139],
    SET_PRODUCT_IMAGE: [93, 232, 202, 90, 159, 75, 96, 240],
    FORCE_ARCHIVE_NFT: [196, 40, 155, 123, 67, 196, 240, 185],
    // Admin instructions
    INITIALIZE_VOTING_ROUND: [26, 78, 176, 62, 179, 90, 209, 81],
    CLOSE_VOTING_ROUND: [62, 39, 86, 97, 226, 93, 14, 51],
    DECLARE_WINNER: [140, 135, 197, 50, 9, 23, 4, 80],
    ARCHIVE_NFT: [102, 151, 35, 14, 90, 23, 91, 92],
    PROMOTE_TO_NEXT_ROUND: [145, 22, 171, 99, 245, 60, 193, 51],
    FORCE_END_ROUND: [167, 40, 148, 212, 87, 253, 241, 252],
    // Auction instructions
    CREATE_AUCTION: [234, 6, 201, 246, 47, 219, 176, 107],
    PLACE_BID: [238, 77, 148, 91, 200, 151, 92, 146],
    FINALIZE_AUCTION: [220, 209, 175, 193, 57, 132, 241, 168],
    CANCEL_AUCTION: [156, 43, 197, 110, 218, 105, 143, 182],
    ADMIN_FORCE_END_AUCTION: [49, 96, 34, 20, 167, 248, 239, 6],
  },
};

// ============================================================================
// POPPER NFT ACCOUNT LAYOUT
// Rust struct: programs/popperverse/src/state.rs - PopperNFT
// Total size: 483 bytes (8 discriminator + 475 fields)
// ============================================================================

export const POPPER_NFT_LAYOUT = {
  SIZE: 483, // Total account size including discriminator

  OFFSETS: {
    // 8-byte discriminator at offset 0
    version: 8,           // u8 (1 byte)
    creatorWallet: 9,     // Pubkey (32 bytes)
    currentOwner: 41,     // Pubkey (32 bytes)
    nftMint: 73,          // Pubkey (32 bytes)
    totalVotes: 105,      // u64 (8 bytes)
    totalVoteSol: 113,    // u64 (8 bytes)
    totalVotePoppers: 121,// u64 (8 bytes)
    votesPerRound: 129,   // [u64; 8] (64 bytes)
    solPerRound: 193,     // [u64; 8] (64 bytes)
    poppersPerRound: 257, // [u64; 8] (64 bytes)
    totalSales: 321,      // u64 (8 bytes)
    totalRoyaltiesPaid: 329, // u64 (8 bytes)
    createdAt: 337,       // i64 (8 bytes)
    createdInRound: 345,  // u8 (1 byte)
    lastActiveRound: 346, // u8 (1 byte)
    isWinner: 347,        // bool (1 byte)
    isArchived: 348,      // bool (1 byte)
    isVotingEligible: 349,// bool (1 byte)
    productId: 350,       // u32 (4 bytes)
    bump: 354,            // u8 (1 byte)
    reserved: 355,        // [u8; 128] (128 bytes) - used for stats storage
  },

  SIZES: {
    version: 1,
    creatorWallet: 32,
    currentOwner: 32,
    nftMint: 32,
    totalVotes: 8,
    totalVoteSol: 8,
    totalVotePoppers: 8,
    votesPerRound: 64,
    solPerRound: 64,
    poppersPerRound: 64,
    totalSales: 8,
    totalRoyaltiesPaid: 8,
    createdAt: 8,
    createdInRound: 1,
    lastActiveRound: 1,
    isWinner: 1,
    isArchived: 1,
    isVotingEligible: 1,
    productId: 4,
    bump: 1,
    reserved: 128,
  },

  // Reserved bytes layout (stats storage within reserved[128])
  RESERVED: {
    productImageUriLength: 0,   // u8 (1 byte) - length of URI string
    productImageUri: 1,         // string (up to 100 bytes)
    // Stats start at byte 101 within reserved
    statsOffset: 101,
    STATS: {
      avgCuteness: 101,         // u16 (2 bytes)
      avgCreativity: 103,       // u16 (2 bytes)
      avgPopPower: 105,         // u16 (2 bytes)
      avgOriginality: 107,      // u16 (2 bytes)
      avgPotential: 109,        // u16 (2 bytes)
      totalCuteness: 111,       // u16 (2 bytes)
      totalCreativity: 113,     // u16 (2 bytes)
      totalPopPower: 115,       // u16 (2 bytes)
      totalOriginality: 117,    // u16 (2 bytes)
      totalPotential: 119,      // u16 (2 bytes)
      statsVoteCount: 121,      // u16 (2 bytes)
    },
    MIN_LENGTH_FOR_STATS: 123,  // Minimum reserved bytes length to have stats
  },
};

// ============================================================================
// AUCTION STATE ACCOUNT LAYOUT
// Rust struct: programs/popperverse/src/auction/state.rs - AuctionState
// Total size: 218 bytes (8 discriminator + 210 fields)
// ============================================================================

export const AUCTION_STATE_LAYOUT = {
  SIZE: 218, // Total account size including discriminator

  OFFSETS: {
    // 8-byte discriminator at offset 0
    nftMint: 8,           // Pubkey (32 bytes)
    seller: 40,           // Pubkey (32 bytes)
    highestBidder: 72,    // Option<Pubkey> (1 + 32 = 33 bytes)
    highestBid: 105,      // u64 (8 bytes)
    minBidIncrement: 113, // u64 (8 bytes)
    startingBid: 121,     // u64 (8 bytes)
    startTime: 129,       // i64 (8 bytes)
    endTime: 137,         // i64 (8 bytes)
    isFinalized: 145,     // bool (1 byte)
    isCancelled: 146,     // bool (1 byte)
    platformFeeBps: 147,  // u16 (2 bytes)
    bidCount: 149,        // u32 (4 bytes)
    bump: 153,            // u8 (1 byte)
    reserved: 154,        // [u8; 64] (64 bytes)
  },

  SIZES: {
    nftMint: 32,
    seller: 32,
    highestBidder: 33,    // Option<Pubkey>: 1 byte discriminant + 32 byte pubkey
    highestBid: 8,
    minBidIncrement: 8,
    startingBid: 8,
    startTime: 8,
    endTime: 8,
    isFinalized: 1,
    isCancelled: 1,
    platformFeeBps: 2,
    bidCount: 4,
    bump: 1,
    reserved: 64,
  },
};

// ============================================================================
// VOTING ROUND ACCOUNT LAYOUT
// Rust struct: programs/popperverse/src/state.rs - VotingRound
// 
// ⚠️ IMPORTANT: Borsh Option<T> uses COMPACT serialization!
//    - None = 1 byte (0x00) 
//    - Some = 1 byte (0x01) + value bytes
//    So offsets AFTER winner_mint are DYNAMIC:
//    - If winner_mint is None: postWinnerOffset = 26 (25 + 1)
//    - If winner_mint is Some: postWinnerOffset = 58 (25 + 1 + 32)
//    Then: is_active = data[postWinnerOffset + 33]
// ============================================================================

export const VOTING_ROUND_LAYOUT = {
  // Fixed offsets (before Option<Pubkey>)
  OFFSETS: {
    // 8-byte discriminator at offset 0
    roundNumber: 8,         // u8 (1 byte)
    startTimestamp: 9,      // i64 (8 bytes)
    endTimestamp: 17,       // i64 (8 bytes)
    winnerMintDiscriminator: 25, // Option discriminator (1 byte: 0=None, 1=Some)
    winnerMintValue: 26,    // Pubkey (32 bytes, ONLY IF hasWinner)
    // After this point, offsets are DYNAMIC based on hasWinner!
    // Use getPostWinnerOffset() helper function
  },
  
  // Relative offsets from postWinnerOffset
  RELATIVE_OFFSETS: {
    totalNftsCreated: 0,    // u64 (8 bytes)
    totalVotesCast: 8,      // u64 (8 bytes)
    totalSolVoted: 16,      // u64 (8 bytes)
    totalPoppersVoted: 24,  // u64 (8 bytes)
    poppersSwapEnabled: 32, // bool (1 byte)
    isActive: 33,           // bool (1 byte)
    authority: 34,          // Pubkey (32 bytes)
    bump: 66,               // u8 (1 byte)
  },

  SIZES: {
    roundNumber: 1,
    startTimestamp: 8,
    endTimestamp: 8,
    winnerMint: 33,  // Max size (1 + 32), actual is 1 if None
    totalNftsCreated: 8,
    totalVotesCast: 8,
    totalSolVoted: 8,
    totalPoppersVoted: 8,
    poppersSwapEnabled: 1,
    isActive: 1,
    authority: 32,
    bump: 1,
  },
  
  // Helper to calculate dynamic offset
  getPostWinnerOffset: (hasWinner) => hasWinner ? 58 : 26,
};

// ============================================================================
// NFT VOTE RECORD ACCOUNT LAYOUT
// Tracks individual user votes for NFTs
// ============================================================================

export const NFT_VOTE_RECORD_LAYOUT = {
  OFFSETS: {
    // 8-byte discriminator at offset 0
    voter: 8,             // Pubkey (32 bytes)
    nftMint: 40,          // Pubkey (32 bytes)
    roundNumber: 72,      // u8 (1 byte)
    voteAmount: 73,       // u64 (8 bytes)
    bump: 81,             // u8 (1 byte)
  },
};

// ============================================================================
// PDA SEEDS
// Used for deriving Program Derived Addresses
// ============================================================================

export const PDA_SEEDS = {
  POPPER_NFT: 'popper_nft',
  VOTING_ROUND: 'voting_round',
  AUCTION_STATE: 'auction',
  BID_ESCROW: 'bid_escrow',
  NFT_ESCROW: 'nft_escrow',
  NFT_VOTE_RECORD: 'nft_vote_record',
};

// ============================================================================
// AUCTION CONSTANTS
// Business logic constants for the auction system
// ============================================================================

export const AUCTION_CONSTANTS = {
  MIN_DURATION: 259_200,        // 3 days in seconds
  MAX_DURATION: 2_592_000,      // 30 days in seconds
  MIN_BID_INCREMENT: 100_000_000, // 0.1 SOL in lamports
  PLATFORM_FEE_BPS: 250,        // 2.5%
  ANTI_SNIPE_WINDOW: 300,       // 5 minutes in seconds
  ANTI_SNIPE_EXTENSION: 300,    // 5 minutes extension
};

// ============================================================================
// PROGRAM CONSTANTS
// Other program-related constants
// ============================================================================

export const PROGRAM_CONSTANTS = {
  LAMPORTS_PER_SOL: 1_000_000_000,
  MAX_NFT_NAME_LENGTH: 24,
  MAX_ROUNDS: 8,                // Maximum voting rounds tracked in arrays
};

// ============================================================================
// HELPER: Check if data matches a discriminator
// ============================================================================

export function matchesDiscriminator(data, discriminator) {
  if (data.length < 8) return false;
  for (let i = 0; i < 8; i++) {
    if (data[i] !== discriminator[i]) return false;
  }
  return true;
}

// ============================================================================
// HELPER: Get account type from data
// ============================================================================

export function getAccountType(data) {
  if (matchesDiscriminator(data, DISCRIMINATORS.AUCTION_STATE)) return 'AuctionState';
  if (matchesDiscriminator(data, DISCRIMINATORS.POPPER_NFT)) return 'PopperNFT';
  if (matchesDiscriminator(data, DISCRIMINATORS.VOTING_ROUND)) return 'VotingRound';
  if (matchesDiscriminator(data, DISCRIMINATORS.NFT_VOTE_RECORD)) return 'NFTVoteRecord';
  return null;
}
